// Rule Form - Add/Edit intelligence rules
import React, { useState, useEffect } from 'react';
import {
  Brain,
  ArrowLeft,
  Save,
  AlertCircle
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

// Mock data - will be replaced with API calls
const mockRules = {
  '1': {
    id: '1',
    name: 'Intro Class Completers - No Follow Up',
    description: 'Students who completed "Intro to Sewing" but haven\'t enrolled in another class',
    category: 'Student Retention',
    condition: {
      type: 'class_completion',
      classTitle: 'Intro to Sewing',
      noFollowUpEnrollment: true,
      daysSinceCompletion: 7
    },
    action: {
      type: 'email',
      templateName: 'Follow-up Class Recommendations',
      autoSend: false
    },
    isActive: true
  }
};

const RuleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    conditionType: 'class_completion',
    classTitle: '',
    noFollowUpEnrollment: true,
    daysSinceCompletion: 7,
    actionType: 'email',
    templateName: '',
    autoSend: false,
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditMode) {
      // Load existing rule
      const rule = mockRules[id];
      if (rule) {
        setFormData({
          name: rule.name,
          description: rule.description,
          category: rule.category,
          conditionType: rule.condition.type,
          classTitle: rule.condition.classTitle || '',
          noFollowUpEnrollment: rule.condition.noFollowUpEnrollment || false,
          daysSinceCompletion: rule.condition.daysSinceCompletion || 7,
          actionType: rule.action.type,
          templateName: rule.action.templateName || '',
          autoSend: rule.action.autoSend || false,
          isActive: rule.isActive
        });
      }
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // TODO: API call to save rule
      console.log('Saving rule:', formData);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Navigate back to intelligence catalog
      navigate('/intelligence');
    } catch (err) {
      console.error('Error saving rule:', err);
      setError('Failed to save rule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/intelligence')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Intelligence
          </button>
        </div>

        <div className="flex items-center gap-3">
          <Brain className="w-8 h-8 text-[#7A99D8]" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditMode ? 'Edit Intelligence Rule' : 'Create Intelligence Rule'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {isEditMode ? 'Update rule conditions and actions' : 'Define conditions and automated actions'}
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 max-w-4xl mx-auto">
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rule Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Intro Class Completers - No Follow Up"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Describe what this rule identifies..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select category...</option>
                  <option value="Student Retention">Student Retention</option>
                  <option value="Revenue">Revenue</option>
                  <option value="Engagement">Engagement</option>
                  <option value="Operations">Operations</option>
                  <option value="Marketing">Marketing</option>
                </select>
              </div>
            </div>
          </div>

          {/* Conditions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Rule Conditions</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Condition Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="conditionType"
                  value={formData.conditionType}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="class_completion">Class Completion</option>
                  <option value="enrollment_status">Enrollment Status</option>
                  <option value="payment_status">Payment Status</option>
                  <option value="attendance">Attendance</option>
                </select>
              </div>

              {formData.conditionType === 'class_completion' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Class Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="classTitle"
                      value={formData.classTitle}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="e.g., Intro to Sewing"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="noFollowUpEnrollment"
                        checked={formData.noFollowUpEnrollment}
                        onChange={handleChange}
                        className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 rounded focus:ring-[#7A99D8] focus:ring-2"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        No follow-up enrollment
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Days Since Completion
                    </label>
                    <input
                      type="number"
                      name="daysSinceCompletion"
                      value={formData.daysSinceCompletion}
                      onChange={handleChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Minimum days since class completion to trigger the rule
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Automated Action</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Action Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="actionType"
                  value={formData.actionType}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="email">Send Email</option>
                  <option value="notification">Send Notification</option>
                  <option value="tag">Add Tag</option>
                </select>
              </div>

              {formData.actionType === 'email' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Template Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="templateName"
                      value={formData.templateName}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="e.g., Follow-up Class Recommendations"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="autoSend"
                        checked={formData.autoSend}
                        onChange={handleChange}
                        className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 rounded focus:ring-[#7A99D8] focus:ring-2"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Automatically send emails (without manual approval)
                      </span>
                    </label>
                    <p className="ml-6 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      If unchecked, emails will require manual approval before sending
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Status</h2>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 rounded focus:ring-[#7A99D8] focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active (rule will run automatically)
                </span>
              </label>
              <p className="ml-6 mt-1 text-xs text-gray-500 dark:text-gray-400">
                Inactive rules can still be run manually but won't execute automatically
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/intelligence')}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-[#7A99D8] hover:bg-[#6A89C8] disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : (isEditMode ? 'Update Rule' : 'Create Rule')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RuleForm;
